import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './brands.css';
import { useTranslation } from 'react-i18next';
import { 
    FaTags, 
    FaPlus, 
    FaEdit, 
    FaTrash, 
    FaSpinner,
    FaSearch,
    FaExclamationTriangle,
    FaEnvelope,
    FaCheck,
    FaTimes,
    FaCode,
    FaCopy
} from 'react-icons/fa';

const Brands = () => {
    const [user, setUser] = useState(null);
    const [brands, setBrands] = useState([]);
    const [filteredBrands, setFilteredBrands] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
    const { t } = useTranslation();

    // Create/Edit brand modal states
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedBrand, setSelectedBrand] = useState(null);
    const [brandFormData, setBrandFormData] = useState({ name: '', description: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState(null);
    const [formSuccess, setFormSuccess] = useState(null);

    // Delete confirmation modal states
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [brandToDelete, setBrandToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Email association modal states
    const [userEmails, setUserEmails] = useState({
        gmail_email: null,
        outlook_emails: []
    });
    const [showEmailSelector, setShowEmailSelector] = useState(false);
    const [selectedBrandForEmail, setSelectedBrandForEmail] = useState(null);
    const [associationLoading, setAssociationLoading] = useState(false);
    const [associationSuccess, setAssociationSuccess] = useState(null);

    useEffect(() => {
        const token = sessionStorage.getItem('access_token');
        if (!token) {
            navigate('/');
            return;
        }

        const fetchData = async () => {
            try {
                setIsLoading(true);
                await fetchUserData(token);
                await fetchBrands(token);
                await fetchUserEmailsData(token);
            } catch (error) {
                console.error('Error loading brands data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [navigate]);

    useEffect(() => {
        // Filter brands based on search term
        if (!searchTerm) {
            setFilteredBrands(brands);
        } else {
            const filtered = brands.filter(brand =>
                brand.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                brand.description.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredBrands(filtered);
        }
    }, [brands, searchTerm]);

    const fetchUserData = async (token) => {
        try {
            const response = await axios.get('/api/users/user', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUser(response.data);
        } catch (error) {
            console.error('Failed to fetch user data:', error);
            setUser(null);
            navigate('/');
        }
    };

    const fetchBrands = async (token) => {
        try {
            const response = await axios.get('/api/brands/list', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBrands(response.data || []);
        } catch (error) {
            console.error('Failed to fetch brands:', error);
            setBrands([]);
        }
    };

    const fetchUserEmailsData = async (token) => {
        try {
            const response = await axios.get('/api/users/user/emails', {
                headers: { Authorization: `Bearer ${token}` },
            });
            setUserEmails(response.data);
        } catch (error) {
            console.error('Failed to fetch user emails:', error);
            setUserEmails({
                gmail_email: null,
                outlook_emails: []
            });
        }
    };

    const canManageBrands = () => {
        return user && ['admin', 'seller', 'eshop'].includes(user.role);
    };

    const handleCreateBrand = () => {
        if (!canManageBrands()) return;
        setBrandFormData({ name: '', description: '' });
        setFormError(null);
        setFormSuccess(null);
        setIsCreateModalOpen(true);
    };

    const handleEditBrand = (brand) => {
        if (!canManageBrands()) return;
        setSelectedBrand(brand);
        setBrandFormData({ name: brand.name, description: brand.description });
        setFormError(null);
        setFormSuccess(null);
        setIsEditModalOpen(true);
    };

    const handleDeleteBrand = (brand) => {
        if (!canManageBrands()) return;
        setBrandToDelete(brand);
        setIsDeleteModalOpen(true);
    };

    const handleFormInputChange = (e) => {
        const { name, value } = e.target;
        setBrandFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmitCreate = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setFormError(null);
        setFormSuccess(null);

        const token = sessionStorage.getItem('access_token');

        try {
            const response = await axios.post('/api/brands/create', brandFormData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setFormSuccess(t('brandCreatedSuccessfully', 'Brand created successfully!'));
            setBrandFormData({ name: '', description: '' });
            await fetchBrands(token);
            
            setTimeout(() => {
                setIsCreateModalOpen(false);
                setFormSuccess(null);
            }, 2000);
        } catch (error) {
            console.error('Failed to create brand:', error);
            setFormError(
                error.response?.data?.detail || 
                t('failedToCreateBrand', 'Failed to create brand. Please try again.')
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmitEdit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setFormError(null);
        setFormSuccess(null);

        const token = sessionStorage.getItem('access_token');

        try {
            await axios.put(`/api/brands/update/${selectedBrand.id}`, brandFormData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setFormSuccess(t('brandUpdatedSuccessfully', 'Brand updated successfully!'));
            await fetchBrands(token);
            
            setTimeout(() => {
                setIsEditModalOpen(false);
                setFormSuccess(null);
                setSelectedBrand(null);
            }, 2000);
        } catch (error) {
            console.error('Failed to update brand:', error);
            setFormError(
                error.response?.data?.detail || 
                t('failedToUpdateBrand', 'Failed to update brand. Please try again.')
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const confirmDelete = async () => {
        setIsDeleting(true);
        const token = sessionStorage.getItem('access_token');

        try {
            await axios.delete(`/api/brands/delete/${brandToDelete.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            await fetchBrands(token);
            setIsDeleteModalOpen(false);
            setBrandToDelete(null);
        } catch (error) {
            console.error('Failed to delete brand:', error);
            alert(
                error.response?.data?.detail || 
                t('failedToDeleteBrand', 'Failed to delete brand. Please try again.')
            );
        } finally {
            setIsDeleting(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleAssociateEmailClick = (brandId) => {
        setSelectedBrandForEmail(brandId);
        setShowEmailSelector(true);
        setAssociationSuccess(null);
    };

    const associateEmailWithBrand = async (email) => {
        if (!selectedBrandForEmail) return;
        
        setAssociationLoading(true);
        setAssociationSuccess(null);
        
        try {
            const token = sessionStorage.getItem('access_token');
            await axios.post(
                '/api/brands/associate-email',
                {
                    email: email,
                    brand_id: selectedBrandForEmail
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            
            setBrands(brands.map(brand => {
                if (brand.id === selectedBrandForEmail) {
                    return { ...brand, associated_email: email };
                }
                return brand;
            }));
            
            setAssociationSuccess(true);
            
            setTimeout(() => {
                setShowEmailSelector(false);
                setSelectedBrandForEmail(null);
                setAssociationSuccess(null);
            }, 2000);
            
        } catch (error) {
            console.error('Failed to associate email with brand:', error);
            setAssociationSuccess(false);
            
            setTimeout(() => {
                setAssociationSuccess(null);
            }, 3000);
        } finally {
            setAssociationLoading(false);
        }
    };

    const removeEmailAssociation = async (brandId, emailToRemove) => {
        setAssociationLoading(true);
        setAssociationSuccess(null);
        
        try {
            const token = sessionStorage.getItem('access_token');
            await axios.post(
                '/api/brands/remove-email-association',
                {
                    email: emailToRemove,
                    brand_id: brandId
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            
            setBrands(brands.map(brand => {
                if (brand.id === brandId) {
                    const updatedBrand = { ...brand };
                    delete updatedBrand.associated_email;
                    return updatedBrand;
                }
                return brand;
            }));
            
            setAssociationSuccess(true);
            
            setTimeout(() => {
                setShowEmailSelector(false);
                setSelectedBrandForEmail(null);
                setAssociationSuccess(null);
            }, 2000);
            
        } catch (error) {
            console.error('Failed to remove email association:', error);
            setAssociationSuccess(false);
            
            setTimeout(() => {
                setAssociationSuccess(null);
            }, 3000);
        } finally {
            setAssociationLoading(false);
        }
    };

    // Replace the handleCopyTicketEmbedCode function with this enhanced version:
    const handleCopyTicketEmbedCode = async (brandId) => {
        const button = document.querySelector(`[data-brand-id="${brandId}"] .una-embed-code-button`);
        
        try {
            const token = sessionStorage.getItem('access_token');
            const params = brandId ? `?brand_id=${brandId}` : '';
            const response = await axios.get(`/api/tickets/embed${params}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.embed_code) {
                navigator.clipboard.writeText(response.data.embed_code);
                
                // Add success class to trigger visual effect
                if (button) {
                    button.classList.add('una-embed-code-success');
                    
                    // Remove success class after 1 second
                    setTimeout(() => {
                        button.classList.remove('una-embed-code-success');
                    }, 1000);
                }
                
                setAssociationSuccess(true);
                setTimeout(() => setAssociationSuccess(null), 3000);
            }
        } catch (error) {
            console.error('Failed to generate and copy embed code:', error);
            setAssociationSuccess(false);
            setTimeout(() => setAssociationSuccess(null), 3000);
        }
    };

    if (isLoading) {
        return (
            <div className="una-brands-loading">
                <FaSpinner className="una-spinner" />
                <h2>{t('loadingBrands', 'Loading brands...')}</h2>
            </div>
        );
    }

    return (
        <div className="una-brands-container">
            <div className="una-brands-header">
                <div className="una-brands-title">
                    <FaTags className="una-brands-icon" />
                    <h1>{t('brandsManagement', 'Brands Management')}</h1>
                </div>
                
                {canManageBrands() && (
                    <button 
                        className="una-create-brand-button"
                        onClick={handleCreateBrand}
                    >
                        <FaPlus />
                        {t('createBrand', 'Create Brand')}
                    </button>
                )}
            </div>

            <div className="una-brands-controls">
                <div className="una-search-container">
                    <FaSearch className="una-search-icon" />
                    <input
                        type="text"
                        placeholder={t('searchBrands', 'Search brands...')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="una-search-input"
                    />
                </div>
            </div>

            <div className="una-brands-content">
                {filteredBrands.length === 0 ? (
                    <div className="una-no-brands">
                        <FaTags className="una-no-brands-icon" />
                        <h3>{t('noBrands', 'No brands found')}</h3>
                        <p>{t('noBrandsDescription', 'No brands match your search criteria.')}</p>
                    </div>
                ) : (
                    <div className="una-brands-grid">
                        {filteredBrands.map(brand => (
                            <div key={brand.id} className="una-brand-card">
                                <div className="una-brand-card-header">
                                    <h3 className="una-brand-name">{brand.name}</h3>
                                    {canManageBrands() && (
                                        <div className="una-brand-actions">
                                            <button
                                                className="una-edit-button"
                                                onClick={() => handleEditBrand(brand)}
                                                title={t('editBrand', 'Edit Brand')}
                                            >
                                                <FaEdit />
                                            </button>
                                            <button
                                                className="una-delete-button"
                                                onClick={() => handleDeleteBrand(brand)}
                                                title={t('deleteBrand', 'Delete Brand')}
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    )}
                                </div>
                                
                                <div className="una-brand-card-content">
                                    <p className="una-brand-description">{brand.description}</p>
                                    
                                    {brand.associated_email && (
                                        <div className="una-brand-email-association">
                                            <FaEnvelope className="una-email-icon" />
                                            <span className="una-associated-email">{brand.associated_email}</span>
                                        </div>
                                    )}
                                    
                                    <div className="una-brand-meta">
                                        <span className="una-brand-date">
                                            {t('created', 'Created')}: {formatDate(brand.created_at)}
                                        </span>
                                        {brand.updated_at !== brand.created_at && (
                                            <span className="una-brand-date">
                                                {t('updated', 'Updated')}: {formatDate(brand.updated_at)}
                                            </span>
                                        )}
                                    </div>
                                    
                                    {canManageBrands() && (
                                        <div className="una-brand-email-actions">
                                            <button
                                                className="una-email-association-button"
                                                onClick={() => handleAssociateEmailClick(brand.id)}
                                                title={brand.associated_email ? t('changeEmail', 'Change Email') : t('associateEmail', 'Associate Email')}
                                            >
                                                <FaEnvelope />
                                                {brand.associated_email ? t('changeEmail', 'Change Email') : t('associateEmail', 'Associate Email')}
                                            </button>
                                            
                                            {/* Replace the existing embed code button with this */}
                                            <div data-brand-id={brand.id}>
                                                <button
                                                    className="una-embed-code-button"
                                                    onClick={() => handleCopyTicketEmbedCode(brand.id)}
                                                    title={t('copyTicketEmbedCode', 'Copy Ticket Form Embed Code')}
                                                >
                                                    <span className="una-embed-button-icon">
                                                        <FaCode />
                                                    </span>
                                                    <span className="una-embed-button-text">
                                                        {t('copyTicketEmbed', 'Copy Ticket Embed')}
                                                    </span>
                                                    <span className="una-embed-button-success">
                                                        <FaCheck />
                                                    </span>
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Create Brand Modal */}
            {isCreateModalOpen && (
                <div className="una-modal-overlay" onClick={() => setIsCreateModalOpen(false)}>
                    <div className="una-modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>{t('createNewBrand', 'Create New Brand')}</h2>
                        <form onSubmit={handleSubmitCreate}>
                            <div className="una-form-group">
                                <label htmlFor="name">{t('brandName', 'Brand Name')} *</label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={brandFormData.name}
                                    onChange={handleFormInputChange}
                                    required
                                    maxLength={100}
                                    placeholder={t('enterBrandName', 'Enter brand name')}
                                />
                            </div>
                            
                            <div className="una-form-group">
                                <label htmlFor="description">{t('description', 'Description')} *</label>
                                <textarea
                                    id="description"
                                    name="description"
                                    value={brandFormData.description}
                                    onChange={handleFormInputChange}
                                    required
                                    maxLength={500}
                                    rows="4"
                                    placeholder={t('enterBrandDescription', 'Enter brand description')}
                                />
                            </div>

                            {formError && <p className="una-error-message">{formError}</p>}
                            {formSuccess && <p className="una-success-message">{formSuccess}</p>}

                            <div className="una-form-actions">
                                <button
                                    type="button"
                                    onClick={() => setIsCreateModalOpen(false)}
                                    className="una-button una-button-secondary"
                                >
                                    {t('cancel', 'Cancel')}
                                </button>
                                <button
                                    type="submit"
                                    className="una-button una-button-primary"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <FaSpinner className="una-spinner" />
                                            {t('creating', 'Creating...')}
                                        </>
                                    ) : (
                                        <>
                                            <FaPlus />
                                            {t('createBrand', 'Create Brand')}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Brand Modal */}
            {isEditModalOpen && selectedBrand && (
                <div className="una-modal-overlay" onClick={() => setIsEditModalOpen(false)}>
                    <div className="una-modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>{t('editBrand', 'Edit Brand')}</h2>
                        <form onSubmit={handleSubmitEdit}>
                            <div className="una-form-group">
                                <label htmlFor="edit-name">{t('brandName', 'Brand Name')} *</label>
                                <input
                                    type="text"
                                    id="edit-name"
                                    name="name"
                                    value={brandFormData.name}
                                    onChange={handleFormInputChange}
                                    required
                                    maxLength={100}
                                    placeholder={t('enterBrandName', 'Enter brand name')}
                                />
                            </div>
                            
                            <div className="una-form-group">
                                <label htmlFor="edit-description">{t('description', 'Description')} *</label>
                                <textarea
                                    id="edit-description"
                                    name="description"
                                    value={brandFormData.description}
                                    onChange={handleFormInputChange}
                                    required
                                    maxLength={500}
                                    rows="4"
                                    placeholder={t('enterBrandDescription', 'Enter brand description')}
                                />
                            </div>

                            {formError && <p className="una-error-message">{formError}</p>}
                            {formSuccess && <p className="una-success-message">{formSuccess}</p>}

                            <div className="una-form-actions">
                                <button
                                    type="button"
                                    onClick={() => setIsEditModalOpen(false)}
                                    className="una-button una-button-secondary"
                                >
                                    {t('cancel', 'Cancel')}
                                </button>
                                <button
                                    type="submit"
                                    className="una-button una-button-primary"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <FaSpinner className="una-spinner" />
                                            {t('updating', 'Updating...')}
                                        </>
                                    ) : (
                                        <>
                                            <FaEdit />
                                            {t('updateBrand', 'Update Brand')}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {isDeleteModalOpen && brandToDelete && (
                <div className="una-modal-overlay" onClick={() => setIsDeleteModalOpen(false)}>
                    <div className="una-modal-content una-delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="una-delete-warning">
                            <FaExclamationTriangle className="una-warning-icon" />
                            <h2>{t('confirmDeleteBrand', 'Confirm Delete Brand')}</h2>
                        </div>
                        
                        <p>
                            {t('deleteBrandConfirmation', 'Are you sure you want to delete the brand')} 
                            <strong> "{brandToDelete.name}"</strong>? 
                            {t('thisActionCannotBeUndone', ' This action cannot be undone.')}
                        </p>

                        <div className="una-form-actions">
                            <button
                                type="button"
                                onClick={() => setIsDeleteModalOpen(false)}
                                className="una-button una-button-secondary"
                            >
                                {t('cancel', 'Cancel')}
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="una-button una-button-danger"
                                disabled={isDeleting}
                            >
                                {isDeleting ? (
                                    <>
                                        <FaSpinner className="una-spinner" />
                                        {t('deleting', 'Deleting...')}
                                    </>
                                ) : (
                                    <>
                                        <FaTrash />
                                        {t('deleteBrand', 'Delete Brand')}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Email Selector Modal */}
            {showEmailSelector && (
                <div className="una-modal-overlay" onClick={() => setShowEmailSelector(false)}>
                    <div className="una-modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>{t('selectEmail', 'Select Email')}</h2>
                        
                        {associationSuccess === true && (
                            <div className="una-success-message">
                                <FaCheck /> {t('emailAssociatedSuccess', 'Email associated successfully!')}
                            </div>
                        )}
                        
                        {associationSuccess === false && (
                            <div className="una-error-message">
                                <FaTimes /> {t('emailAssociationError', 'Failed to associate email. Please try again.')}
                            </div>
                        )}
                        
                        {associationSuccess === null && (
                            <>
                                <div className="una-email-options">
                                    {[
                                        userEmails.gmail_email, 
                                        ...(userEmails.outlook_emails || [])
                                    ].filter(email => email).map((email, index) => (
                                        <button 
                                            key={index}
                                            className="una-email-option-button"
                                            onClick={() => associateEmailWithBrand(email)}
                                            disabled={associationLoading}
                                        >
                                            <FaEnvelope />
                                            {email}
                                        </button>
                                    ))}
                                    
                                    {brands.find(b => b.id === selectedBrandForEmail)?.associated_email && (
                                        <button 
                                            className="una-email-option-button una-remove-email-button"
                                            onClick={() => {
                                                if (window.confirm(t('confirmRemoveEmail', 'Are you sure you want to remove the email association?'))) {
                                                    const brand = brands.find(b => b.id === selectedBrandForEmail);
                                                    removeEmailAssociation(selectedBrandForEmail, brand.associated_email);
                                                }
                                            }}
                                            disabled={associationLoading}
                                        >
                                            <FaTimes />
                                            {t('removeEmail', 'Remove Email')}
                                        </button>
                                    )}
                                </div>
                                
                                <div className="una-form-actions">
                                    <button 
                                        type="button"
                                        onClick={() => {
                                            setShowEmailSelector(false);
                                            setSelectedBrandForEmail(null);
                                        }}
                                        className="una-button una-button-secondary"
                                    >
                                        {t('cancel', 'Cancel')}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Brands;